import { $t } from '@/i18n';
import { computed, defineComponent, onBeforeUnmount, onMounted, ref, toRefs } from '@vue/composition-api';
import screenInfo from '@/utils/screen';
import styles from './styles/code-conflict-editor-view.module.scss';
import { message } from '@utils/wuji-message';
import monacoProvider from '@utils/monacoProvider';
import useEditConflict from './hooks/useEditConflict';
import { useDataFormatter } from './hooks/useDataFormatter';
/**
 * 代码冲突编辑器视图组件
 * 用于展示和解决代码合并冲突
 *
 * 功能特性：
 * - Monaco 编辑器支持
 * - 冲突标记高亮显示
 * - 冲突导航（上一个/下一个）
 * - YAML/JSON 格式校验
 * - 手动标记已解决
 */
export default defineComponent({
    name: 'CodeConflictEditorView',
    props: {
        // 冲突数据对象
        conflictItem: {
            type: Object,
            default: () => ({}),
        },
        // 编辑器内容类型（yaml/json）
        type: {
            type: String,
            default: 'yaml',
        },
        // 当前分支标签
        currentBranchLabel: {
            type: String,
            default: '',
        },
        // 合并分支标签
        mergeBranchLabel: {
            type: String,
            default: '',
        },
        // 组件唯一键
        key: {
            type: String,
            default: '',
        },
    },
    emits: ['change'],
    setup(props, { emit }) {
        const { type } = toRefs(props);
        const editorRef = ref();
        const editor = ref();
        const resizeObserver = ref();
        const isNarrowNavbar = computed(() => screenInfo.screen.xs || screenInfo.screen.sm);
        const { updateConflicts, findMatch, conflictsPosition, } = useEditConflict({
            currentLabel: props.currentBranchLabel,
            incomingLabel: props.mergeBranchLabel,
            editor,
            classname: {
                conflict_current_highlight: styles.conflict_current_highlight,
                conflict_incoming_highlight: styles.conflict_incoming_highlight,
            },
            onAccept: () => {
                updateConflicts(false);
                emit('change', editor.value.getValue());
            },
        });
        /**
         * 处理内容变化
         * 当编辑器失焦时触发
         */
        const handleChangeConflict = (data) => {
            emit('change', data);
        };
        /**
         * 手动标记冲突为已解决
         *
         * 校验逻辑：
         * 1. 检查是否还有未解决的冲突标记
         * 2. 根据类型进行格式校验（YAML/JSON）
         * 3. 校验通过后发送 resolved 信号
         */
        const handleMarkResolved = () => {
            // 检查是否还有未解决的冲突
            if (conflictsPosition.value.length > 0) {
                message.warning($t('k_5bf0cfa1', { arg_0: conflictsPosition.value.length }));
                return;
            }
            const content = editor.value.getValue();
            const { validateYAML, validateJSON } = useDataFormatter();
            // 根据类型进行格式校验
            const validation = type.value === 'yaml'
                ? validateYAML(content)
                : validateJSON(content);
            if (!validation.valid) {
                const errorType = type.value === 'yaml' ? 'YAML' : 'JSON';
                let errorMsg = $t('k_36b0947e', { arg_0: errorType });
                // 添加位置信息
                if (validation.line && validation.column) {
                    errorMsg += $t('k_53299515', { arg_0: validation.line, arg_1: validation.column });
                }
                message.error(errorMsg);
                return;
            }
            // 发送已解决信号（包含数据和 resolved 标志）
            emit('change', { data: content, resolved: true });
        };
        /**
         * 初始化 Monaco 编辑器
         */
        onMounted(() => {
            monacoProvider((monaco) => {
                const model = monaco.editor.createModel(props.conflictItem.after, props.type);
                editor.value = monaco.editor.create(editorRef.value, {
                    model,
                    theme: 'vs',
                    automaticLayout: true,
                });
                // 初始化时更新冲突标记
                updateConflicts(true);
                // 监听内容变化，实时更新冲突位置
                editor.value.onDidChangeModelContent(() => {
                    updateConflicts(false);
                });
                // 监听编辑器失焦，触发内容变化
                editor.value.onDidBlurEditorText(() => {
                    updateConflicts(false);
                    handleChangeConflict(editor.value.getValue());
                });
                if (editorRef.value) {
                    const observer = new ResizeObserver(() => {
                        editor.value?.layout();
                    });
                    observer.observe(editorRef.value);
                    resizeObserver.value = observer;
                }
            });
        });
        onBeforeUnmount(() => {
            resizeObserver.value?.disconnect();
            editor.value?.dispose();
        });
        return {
            editorRef,
            findMatch,
            conflictsPosition,
            handleMarkResolved,
            isNarrowNavbar,
        };
    },
    render() {
        return (<div class={styles.code_merge_container}>
        {/* 顶部导航栏 */}
        <div class={styles.navbar}>
          <div class={styles.navbarTitle}>{$t('k_743cd779')}</div>
          <div class={styles.navbarActions}>
            {/* 冲突状态提示 */}
            {this.conflictsPosition.length === 0 ? (<span class={styles.conflict_message_resolved}>
                {this.isNarrowNavbar
                    ? $t('k_9f4c05a')
                    : $t('k_47b9d88')}
              </span>) : (<span class={styles.conflict_message}>
                {this.conflictsPosition.length}{$t('k_722f2bb5')}
              </span>)}

            {/* 冲突导航按钮 */}
            {this.conflictsPosition.length >= 1 && (<a-button type="link" onClick={() => this.findMatch('Previous')}>
                {$t('k_9346c618')}
                <a-icon type="up"/>
              </a-button>)}
            {this.conflictsPosition.length >= 1 && (<a-button type="link" onClick={() => this.findMatch('Next')}>
                {$t('k_c0a9baee')}
                <a-icon type="down"/>
              </a-button>)}

            {/* 标记已解决按钮 */}
            <a-button type="primary" onClick={this.handleMarkResolved} disabled={this.conflictsPosition.length > 0}>
              {$t('k_cb2316e1')}
            </a-button>
          </div>
        </div>

        {/* Monaco 编辑器容器 */}
        <div class={styles.conflict_content}>
          <div ref="editorRef" class={styles.editorHost}></div>
        </div>
      </div>);
    },
});
